import { cva, type VariantProps } from "class-variance-authority"

import { typographyVariants } from "@/components/ds/Typography"
import { cn } from "@/lib/utils"

export const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center transition-colors select-none outline-none rounded-full h-fit gap-2",
    "[&>svg:not([width])]:w-6 [&>svg:not([height])]:h-6",
    "disabled:cursor-not-allowed",
    "aria-disabled:cursor-not-allowed",
    typographyVariants({
      variant: "body1",
      weight: "medium",
    }),
  ),
  {
    variants: {
      variant: {
        primary: cn(
          "bg-brand-primary text-white",
          "hover:bg-brand-hover",
          "active:bg-brand-pressed",
          "disabled:bg-brand-primary disabled:opacity-30",
          "disabled:hover:bg-brand-primary disabled:active:bg-brand-primary",
          "aria-disabled:bg-brand-primary aria-disabled:opacity-30",
          "aria-disabled:hover:bg-brand-primary aria-disabled:active:bg-brand-primary",
        ),
        secondary: cn(
          "bg-brand-mainBg text-brand-primary",
          "hover:bg-brand-secondBg hover:text-brand-hover",
          "active:bg-brand-secondBg active:text-brand-pressed",
          "disabled:bg-brand-mainBg disabled:opacity-30",
          "disabled:hover:bg-brand-mainBg disabled:hover:text-brand-primary disabled:active:bg-brand-mainBg disabled:active:text-brand-primary",
          "aria-disabled:bg-brand-mainBg aria-disabled:opacity-30",
          "aria-disabled:hover:bg-brand-mainBg aria-disabled:hover:text-brand-primary aria-disabled:active:bg-brand-mainBg aria-disabled:active:text-brand-primary",
        ),
        outline: cn(
          "bg-transparent border border-brand-mainBg text-brand-primary",
          "hover:border-brand-primary hover:bg-neutral-secondBg hover:text-brand-hover",
          "active:border-brand-primary active:bg-neutral-border active:text-brand-pressed",
          "disabled:text-neutral-title disabled:border-brand-mainBg disabled:opacity-30",
          "disabled:hover:border-brand-mainBg disabled:hover:bg-transparent disabled:hover:text-neutral-title disabled:active:border-brand-mainBg disabled:active:bg-transparent disabled:active:text-neutral-title",
          "aria-disabled:text-neutral-title aria-disabled:border-brand-mainBg aria-disabled:opacity-30",
          "aria-disabled:hover:border-brand-mainBg aria-disabled:hover:bg-transparent aria-disabled:hover:text-neutral-title aria-disabled:active:border-brand-mainBg aria-disabled:active:bg-transparent aria-disabled:active:text-neutral-title",
        ),
        text: cn(
          "bg-transparent text-brand-primary",
          "hover:bg-neutral-secondBg hover:text-brand-hover",
          "active:bg-neutral-border active:text-brand-pressed",
          "disabled:opacity-30",
          "disabled:hover:bg-transparent disabled:hover:text-brand-primary disabled:active:bg-transparent disabled:active:text-brand-primary",
          "aria-disabled:opacity-30",
          "aria-disabled:hover:bg-transparent aria-disabled:hover:text-brand-primary aria-disabled:active:bg-transparent aria-disabled:active:text-brand-primary",
        ),
        transparent: cn(
          "bg-neutral-title/40 text-white",
          "active:bg-neutral-title/60",
          "disabled:opacity-30",
          "disabled:active:bg-neutral-title/40",
          "aria-disabled:opacity-30",
          "aria-disabled:active:bg-neutral-title/40",
        ),
        danger: cn(
          "bg-danger-primary text-white",
          "hover:bg-danger-hover",
          "active:bg-danger-pressed",
          "disabled:bg-danger-primary disabled:opacity-30",
          "disabled:hover:bg-danger-primary disabled:active:bg-danger-primary",
          "aria-disabled:bg-danger-primary aria-disabled:opacity-30",
          "aria-disabled:hover:bg-danger-primary aria-disabled:active:bg-danger-primary",
        ),
      },
      size: {
        large: "",
        medium: "",
        small: "",
      },
      square: {
        true: "aspect-square",
        false: "min-w-[150px]",
      },
    },
    compoundVariants: [
      {
        size: "large",
        square: false,
        class: "px-6 py-4",
      },
      {
        size: "medium",
        square: false,
        class: "px-4 py-3",
      },
      {
        size: "small",
        square: false,
        class: "px-3 py-2",
      },
      {
        size: "large",
        square: true,
        class: "p-4",
      },
      {
        size: "medium",
        square: true,
        class: "p-3",
      },
      {
        size: "small",
        square: true,
        class: "p-2",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "medium",
      square: false,
    },
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
